import { Link, RecordContextProvider, useGetMany, useGetManyReference } from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { atom, useRecoilState } from "recoil";
import { useMemo, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Row } from "./Row";
import { StudentAvatar } from "./StudentAvatar";
import { StudentDisplayName } from "./StudentDisplayName";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Student } from "../providers/studentsProvider";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSetReadyForTheoryExamMutation } from "../api/backoffice.api";
import { DateTime } from "luxon";
import { TheoryLesson } from "../model/autovioCalendarEvents";
import { Training } from "../providers/catalogProvider";
import { StudentTheoryLearningProgressField } from "../fields/StudentTheoryLearningProgressField";
import { StudentTheoryExamSimulationsField } from "../fields/StudentTheoryExamSimulationsField";
import { Column } from "./Column";
import { DialogCloseButton } from "./DialogCloseButton";
import { useMenu } from "../hooks/useMenu";
import { formatDate } from "../utils/calendar";
import { EventInfoRow } from "../dialogs/EventDialog/EventInfoRow";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";

export const studentInPreTheoryExamWorkflowState = atom<null | Student>({
  key: "studentInPreTheoryExamWorkflowState",
  default: null,
  effects: [
    ({ onSet }) => {
      onSet((newValue) => {
        const url = new URL(location.href);
        if (newValue) {
          if (url.searchParams.get("openDialog") !== `PreTheoryExamWorkflowDialog!${newValue.id}`) {
            url.searchParams.set("openDialog", `PreTheoryExamWorkflowDialog!${newValue.id}`);
            browserHistory.replace(url.toString());
          }
        } else if (url.searchParams.has("openDialog")) {
          url.searchParams.delete("openDialog");
          browserHistory.replace(url.toString());
        }
      });
    },
  ],
});

export function PreTheoryExamWorkflowDialog() {
  const [student, setStudentShownInDialog] = useRecoilState(studentInPreTheoryExamWorkflowState);
  const closeDialog = () => setStudentShownInDialog(null);
  const isDialogOpen = !!student;
  const [saving, setSaving] = useState(false);
  const setReadyForTheoryExamMutation = useSetReadyForTheoryExamMutation();

  const markStudentAsReadyForTheoryExam = async () => {
    if (!student) {
      throw new Error(`Unexpected state: student: ${student}`);
    }
    setSaving(true);
    let success = false;
    try {
      await setReadyForTheoryExamMutation.mutateAsync({ studentUid: student.id, isReady: true });
      success = true;
    } catch (error) {
      console.error(`Failed to mark student ${student.id} as ready for theory exam`, error);
      showErrorToast(`Fehler beim Starten der Organisation der Theorieprüfung von ${student.name}.`);
    }
    if (success) {
      closeDialog();
      showSuccessToast(`${student.name} wurde erfolgreich benachrichtigt, Terminwünsche anzugeben.`);
    }
  };

  return (
    <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>Theorieprüfung</DialogTitle>
      {student && <_PopupMenu student={student} closeDialog={closeDialog} />}
      <DialogCloseButton onClick={closeDialog} />
      <DialogContent>{student && <_StudentData student={student} />}</DialogContent>
      <DialogActions>
        {student && !student.isReadyForTheoryExam && (
          <LoadingButton variant="contained" loading={saving} onClick={markStudentAsReadyForTheoryExam}>
            Organisation starten
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

function _StudentData({ student }: { student: Student }) {
  const { data: theoryLessons } = useGetManyReference<TheoryLesson>("theoryLessons", {
    target: "studentUids",
    id: student.id,
  });
  const attendedTheoryLessons = theoryLessons?.filter((it) => it.students[student.id]?.attended);
  const { data: trainings } = useGetMany<Training>("trainings", {
    ids: student.bookedTrainings.map((it) => it.trainingId),
  });
  const numberOfRequiredTheoryLessons: undefined | number = useMemo(() => {
    if (trainings && trainings.length === student.bookedTrainings.length) {
      try {
        return _getNumberOfRequiredTheoryLessonsForTheoryExam(trainings);
      } catch (error) {
        console.error(`Failed to compute number of required theory lessons for student ${student.id}`, error);
        return undefined;
      }
    }
  }, [student, trainings]);

  return (
    <RecordContextProvider value={student}>
      <Column
        spacing="0.5em"
        sx={{ bgcolor: "action.hover", borderRadius: 1, color: "text.secondary", p: 2, "& a": { color: "inherit" } }}
      >
        <Typography variant="body2" fontWeight="bold" color="text.primary">
          Details
        </Typography>
        <EventInfoRow>
          <Link
            to={`/students/${student.id}`}
            target="_blank"
            sx={{ alignItems: "center", display: "flex", textDecoration: "none" }}
          >
            <Tooltip title="Fahrschüler" arrow placement="top">
              <span style={{ display: "inline-flex" }}>
                <StudentAvatar studentId={student.id} size="20px" />
              </span>
            </Tooltip>
            <Row spacing="0.5em" sx={{ ml: "0.5em" }}>
              <StudentDisplayName variant="body2" studentId={student.id} />
              {student.isSchoolChanger && <Typography variant="body2">(Fahrschulwechsler)</Typography>}
            </Row>
            <OpenInNewIcon sx={{ fontSize: "16px", ml: "0.5em" }} />
          </Link>
        </EventInfoRow>
        {student.dateOfBirth && (
          <EventInfoRow>
            <Tooltip title="Geburtsdatum" arrow placement="top">
              <CakeOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </Tooltip>
            <Typography variant="body2">{formatDate(student.dateOfBirth)}</Typography>
          </EventInfoRow>
        )}
        <EventInfoRow>
          <Tooltip title="Prüfauftrag eingegangen am" arrow placement="top">
            <AssignmentOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Tooltip>
          <Typography variant="body2">
            {student.examinationAssignmentReceivedAt
              ? `${DateTime.fromISO(student.examinationAssignmentReceivedAt).toFormat("dd.MM.yyyy")} (Prüfauftrag eingegangen)`
              : "Prüfauftrag noch nicht eingegangen"}
          </Typography>
        </EventInfoRow>
        <EventInfoRow>
          <Tooltip title="Besuchte Theoriestunden" arrow placement="top">
            <SchoolOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Tooltip>
          <Typography variant="body2">
            {attendedTheoryLessons?.length ?? "..."} / {numberOfRequiredTheoryLessons ?? "..."}
          </Typography>
        </EventInfoRow>
        <EventInfoRow>
          <Tooltip title="Lernstand" arrow placement="top">
            <TrendingUpIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Tooltip>
          <StudentTheoryLearningProgressField />
        </EventInfoRow>
        <EventInfoRow>
          <Tooltip title="Prüfungssimulationen" arrow placement="top">
            <QuizOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Tooltip>
          <Column sx={{ flex: 1, minWidth: 0 }}>
            <StudentTheoryExamSimulationsField emptyText="Keine Prüfungssimulationen" />
          </Column>
        </EventInfoRow>
      </Column>
    </RecordContextProvider>
  );
}

function _PopupMenu({ student, closeDialog }: { student: Student; closeDialog: () => void }) {
  const { anchorEl, openMenu, closeMenu } = useMenu();
  const [saving, setSaving] = useState(false);
  const setReadyForTheoryExamMutation = useSetReadyForTheoryExamMutation();

  const markStudentAsNotReadyForTheoryExam = async () => {
    setSaving(true);
    let success = false;
    try {
      await setReadyForTheoryExamMutation.mutateAsync({ studentUid: student.id, isReady: false });
      success = true;
    } catch (error) {
      console.error(`Failed to mark student ${student.id} as not ready for theory exam`, error);
      showErrorToast("Fehler beim Löschen der Anfrage.");
    } finally {
      closeMenu();
      // [UX] the menu item should stay disabled while the close menu animation is running ...
      setTimeout(() => setSaving(false), 500);
    }
    if (success) {
      closeDialog();
      showSuccessToast("Anfrage erfolgreich gelöscht.");
    }
  };

  return (
    <div>
      <IconButton sx={{ position: "absolute", top: "17px", right: "57px" }} onClick={openMenu}>
        <MoreVertIcon sx={{ color: "text.secondary" }} />
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MenuItem onClick={markStudentAsNotReadyForTheoryExam} disabled={saving}>
          <ListItemIcon>{saving ? <CircularProgress size={24} color="inherit" /> : <DeleteOutlineIcon />}</ListItemIcon>
          <ListItemText>Anfrage löschen</ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
}

type TheoryLessonType =
  | "Theorieunterricht_Grundstoff"
  | "Theorieunterricht_Zusatzstoff_A"
  | "Theorieunterricht_Zusatzstoff_B";

function _getCompulsoryTheoryLessons(training: Training): { [key in TheoryLessonType]?: number } {
  return Object.fromEntries(
    training.compulsoryLessons
      .map(({ productId, count }) => {
        const product = training.products.find((it) => it.id === productId)!;
        return [product.type, count];
      })
      .filter(([type]) => {
        return (
          type === "Theorieunterricht_Grundstoff" ||
          type === "Theorieunterricht_Zusatzstoff_A" ||
          type === "Theorieunterricht_Zusatzstoff_B"
        );
      }),
  );
}

function _getNumberOfRequiredTheoryLessonsForTheoryExam(trainings: Array<Training>): number {
  const accumulatedCompulsoryTheoryLessons: { [key in TheoryLessonType]?: number } = {};
  for (const training of trainings) {
    const compulsoryTheoryLessons = _getCompulsoryTheoryLessons(training);
    for (const [type, n] of Object.entries(compulsoryTheoryLessons) as Array<[TheoryLessonType, number]>) {
      accumulatedCompulsoryTheoryLessons[type] = Math.max(compulsoryTheoryLessons[type] ?? 0, n);
    }
  }
  return Object.values(accumulatedCompulsoryTheoryLessons).reduce((a, b) => a + b, 0);
}
